import { useCallback, useEffect, useMemo, useState } from 'react'
import {
	getAssignModalViewAfterSelectionChange,
	getReassignmentStudents,
	shouldShowReassignmentWarning,
} from '../assignModalHelpers'

export const useAssignModal = ({
	open,
	selectedStudents,
	onRemoveFromSelection,
	onClose,
}) => {
	const [view, setView] = useState('assign')
	const [selectedCounselorId, setSelectedCounselorId] = useState(null)

	const selectedList = useMemo(
		() => (Array.isArray(selectedStudents) ? selectedStudents : []),
		[selectedStudents],
	)

	const reassignmentStudents = useMemo(
		() => getReassignmentStudents(selectedList),
		[selectedList],
	)

	const showWarning = shouldShowReassignmentWarning(selectedList)
	const assignCount = selectedList.length
	const reassignmentCount = reassignmentStudents.length

	useEffect(() => {
		if (!open) {
			setView('assign')
			setSelectedCounselorId(null)
			return
		}
		setView(current =>
			getAssignModalViewAfterSelectionChange({
				view: current,
				selectedStudents: selectedList,
			}),
		)
	}, [open, selectedList])

	useEffect(() => {
		if (open && assignCount === 0) {
			onClose?.()
		}
	}, [assignCount, onClose, open])

	const goToReassignView = useCallback(() => {
		if (reassignmentCount === 0) return
		setView('reassign')
	}, [reassignmentCount])

	const goToAssignView = useCallback(() => {
		setView('assign')
	}, [])

	const removeAssignedFromSelection = useCallback(() => {
		const ids = reassignmentStudents.map(student => student.id)
		if (!ids.length) return
		onRemoveFromSelection?.(ids)
		setView('assign')
	}, [onRemoveFromSelection, reassignmentStudents])

	const removeOneFromSelection = useCallback(
		studentId => {
			onRemoveFromSelection?.([studentId])
		},
		[onRemoveFromSelection],
	)

	const selectCounselor = useCallback(counselorId => {
		setSelectedCounselorId(counselorId)
	}, [])

	return {
		view,
		selectedCounselorId,
		showWarning,
		assignCount,
		reassignmentCount,
		reassignmentStudents,
		goToReassignView,
		goToAssignView,
		removeAssignedFromSelection,
		removeOneFromSelection,
		selectCounselor,
	}
}
